import { Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IRecipient } from '../../../models/INotifications';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { get, post } from 'aws-amplify/api';

/**
 * Path segment for GET/POST ad-hoc participant flows (`adhoc/{category}/recipients/…`).
 * Must match API Gateway / backend route spelling.
 */
const ADHOC_CATEGORY_RECIPIENTS_SEGMENT = 'recipients';

/**
 * Separate POST route for email recipient payloads (`adhoc/recipients`). Not the same resource as {@link ADHOC_CATEGORY_RECIPIENTS_SEGMENT}.
 */
const ADHOC_POST_RECIPIENTS_PATH = 'adhoc/recipients';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  constructor(private http: HttpClient) {}

  /**
   * encodeURIComponent leaves `'` unescaped (ECMAScript); WAFs often reject raw quotes in paths.
   * Restore literal commas after encoding so comma-separated category values keep working.
   * Uses case-insensitive `%2c` match for robustness; `gi` is fine here (not a hot path).
   */
  private encodePathSegment(value: unknown): string {
    return encodeURIComponent(String(value))
      .replace(/'/g, '%27')
      .replace(/%2c/gi, ',');
  }

  getRecipients(category: string, categoryValue: any, dtStart?: string, dtEnd?: string): Observable<IRecipient[]> {
    // POST for session/guest
    if (category === 'session' || category === 'guest') {
      const body = { ParticipantID: categoryValue };
      const op = post({
        apiName: 'portal',
        path: `adhoc/${category}/${ADHOC_CATEGORY_RECIPIENTS_SEGMENT}`,
        options: { body: JSON.parse(JSON.stringify(body)) },
      });
      return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
        catchError((error) => throwError(() => error)),
        map((data) => data as IRecipient[])
      );
    }
    // GET for meeting
    if (category === 'meeting') {
      const filter = `${dtStart},${dtEnd}`;
      const encodedCategoryValue = this.encodePathSegment(categoryValue);
      const op = get({
        apiName: 'portal',
        path: `adhoc/${category}/${ADHOC_CATEGORY_RECIPIENTS_SEGMENT}/${encodedCategoryValue}/filter/${filter}`,
      });
      return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
        catchError((error) => throwError(() => error)),
        map((data) => data as IRecipient[])
      );
    }
    // default GET
    const op = get({
      apiName: 'portal',
      path: `adhoc/${category}/${ADHOC_CATEGORY_RECIPIENTS_SEGMENT}/${this.encodePathSegment(categoryValue)}`,
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data) => data as IRecipient[])
    );
  }
  // can bimal make change to not pass comma list.
  getCategoryFilterValues(category: string, dtStart: string, dtEnd: string): Observable<any> {
    const filter = `${dtStart},${dtEnd}`;
    const op = get({
      apiName: 'portal',
      path: `adhoc/${category}/filter/${filter}`,
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(map((data) => data));
  }
  public sendNotification(
    category: string,
    messageType: string,
    msgTitle: string,
    notificationBody: string,
    participants: any,
    senderEmail: string,
    categoryItemNames: any,
    alertBody: string,
    sendType: string,
    scheduleDateTime: string,
    isAllSelected: boolean,
    dtStart: string,
    dtEnd: string
  ): Observable<any> {
    if (category === 'guest' || category === 'email') {
      if (category === 'email') {
        isAllSelected = false;
        const currentDate = new Date();
        const dd = String(currentDate.getDate()).padStart(2, '0');
        const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
        const yyyy = currentDate.getFullYear();
        const formattedDate = `${yyyy}-${mm}-${dd}`;
        dtStart = formattedDate;
        dtEnd = formattedDate;
      }
      // Validate dtStart and dtEnd
      if (!dtStart || isNaN(Date.parse(dtStart))) {
        return throwError(() => new Error('Invalid dtStart parameter'));
      }
      if (!dtEnd || isNaN(Date.parse(dtEnd))) {
        return throwError(() => new Error('Invalid dtEnd parameter'));
      }
      categoryItemNames = [
        {
          name: category,
          id: 0,
          dtStart: `${dtStart} 00:00:00`,
          dtEnd: `${dtEnd} 23:59:59`,
        },
      ];
    }
    const messageData = {
      category,
      categoryId: '999',
      recipients: isAllSelected
        ? []
        : typeof participants === 'string' && participants.trim().length > 0
          ? participants.split(',').map(Number)
          : [],
      allUsers: isAllSelected,
      attributes: categoryItemNames,
      message: {
        messageType,
        msgTitle,
        notificationBody,
        alertBody,
        senderEmail,
      },
      sendType,
      scheduleDateTime: sendType !== 'schedule' ? undefined : scheduleDateTime,
    };
    const op = post({
      apiName: 'portal',
      path: `adhoc/message`,
      options: { body: JSON.parse(JSON.stringify(messageData)) },
    });
    return from(op.response.then(async (r) => (await r.body) as any));
  }
  encodeHTML(html: string): string {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  utf8ToLatin1(str: string) {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder('iso-8859-1', { fatal: true });
    const utf8Bytes = encoder.encode(str);
    return decoder.decode(utf8Bytes);
  }
  postEmailRecipients(emails: any): Observable<any> {
    const body = {
      category: 'email',
      attributes: emails,
    };
    const op = post({
      apiName: 'portal',
      path: ADHOC_POST_RECIPIENTS_PATH,
      options: { body },
    });
    return from(op.response).pipe(
      switchMap((r) => from(r.body.json())),
    );
  }

  public fileUploadParticipants(fileBase64: string) {
    const op = post({
      apiName: 'portal',
      path: 'adhoc/file-upload',
      options: { body: JSON.parse(JSON.stringify(fileBase64)) },
    });
    return from(op.response).pipe(
      switchMap((r) => from(r.body.json()))
    );
  }
}
